import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import StickyTableContainer from "../ui/StickyTableContainer";

const SuivrDeProd = () => {
    const [chains, setChains] = useState([]);
    const [selectedChain, setSelectedChain] = useState("");
    const [models, setModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState("");
    const [error, setError] = useState("");
    const [model, setModel] = useState(null);
    const [planningData, setPlanningData] = useState({});
    const [plannedHoursByDate, setPlannedHoursByDate] = useState({});
    const [productionData, setProductionData] = useState({});
    const [workedHoursByDate, setWorkedHoursByDate] = useState({})

    const comparisonDates = Array.from(new Set([
        ...Object.keys(planningData),
        ...Object.keys(productionData),
        ...Object.keys(workedHoursByDate),
    ])).sort();

    const visibleDates = comparisonDates.filter((date) => {
        const realProduction = Number(productionData[date] || 0);
        const plannedProduction = Number(planningData[date] || 0);
        const dayOfWeek = new Date(`${date}T00:00:00`).getDay();

        return !(
            (dayOfWeek === 0 || dayOfWeek === 6)
            && realProduction === 0
            && plannedProduction === 0
        );
    });

    let cumulativeReal = 0;
    let cumulativePlanned = 0;
    const trackingRows = visibleDates.map((date) => {
        const realProduction = Number(productionData[date] || 0);
        const plannedProduction = Number(planningData[date] || 0);
        cumulativeReal += realProduction;
        cumulativePlanned += plannedProduction;

        return {
            date,
            day: new Date(`${date}T00:00:00`).toLocaleDateString("fr-FR", { weekday: "long" }),
            realProduction,
            plannedProduction,
            cumulReal: cumulativeReal,
            cumulPlanned: cumulativePlanned,
            difference: cumulativeReal - cumulativePlanned,
            realWorkedHours: Number(workedHoursByDate[date] || 0),
            plannedWorkedHours: Number(plannedHoursByDate[date] || 0),
        };
    });

    useEffect(() => {
        const fetchChains = async () => {
            try {
                const response = await api.get("/chains");
                setChains(response.data);
            } catch (error) {
                console.error("Error fetching chains:", error);
            }
        };

        fetchChains();
    }, []);

    useEffect(() => {
        const fetchModelsByChain = async () => {
            if (selectedChain) {
                try {
                    const response = await api.get(`/models-by-chain/${selectedChain}`);
                    const chainModels = response.data || [];
                    const latestModel = chainModels[0] || null;
                    setModels(chainModels);
                    setSelectedModel(latestModel ? String(latestModel.id) : "");
                    setModel(latestModel);
                } catch (error) {
                    console.error("Error fetching models by chain:", error);
                    setModels([]);
                    setSelectedModel("");
                    setModel(null);
                }
            }
        };

        fetchModelsByChain();
    }, [selectedChain]);

    useEffect(() => {
        if (!model || !selectedChain) return;
        fetchPlanningData(model.id);
        fetchWorkedHours(model.id);
    }, [model, selectedChain]);

    const handleSelectChange = (e) => {
        setSelectedChain(e.target.value);
        setModels([]);
        setSelectedModel("");
        setModel(null); // Reset model on chain change
        setPlanningData({});
        setPlannedHoursByDate({});
        setProductionData({});
        setWorkedHoursByDate({});
    };

    const handleModelChange = (e) => {
        const modelId = e.target.value;
        setSelectedModel(modelId);
        setModel(models.find((item) => String(item.id) === modelId) || null);
        setPlanningData({});
        setPlannedHoursByDate({});
        setProductionData({});
        setWorkedHoursByDate({});
    };

    const fetchPlanningData = async (modelId) => {
        setError(null);
        try {
            // Use different endpoint based on whether chain is selected
            const planningUrl = selectedChain 
                ? `/product_plans_model_dash_chain/${modelId}/${selectedChain}`
                : `/product_plans_model_dash/${modelId}`;
            
            const planningResponse = await api.get(planningUrl);
            // Keep actual production scoped to the same chain as the planning data.
            // Without this filter the API returns this model's sortie from every
            // chain, which inflates (and can effectively double) the dashboard.
            const productionResponse = await api.get(`/export_total/${modelId}`, {
                params: { chain_id: selectedChain },
            });

            console.log(planningResponse.data)
            console.log(productionResponse.data)

            const dailyPlanning = planningResponse.data.planning || {};

            setPlanningData(dailyPlanning);
            setPlannedHoursByDate(planningResponse.data.planned_hours_by_date || {});
            setProductionData(productionResponse.data.planning || {});
        } catch (error) {
            console.error(error);
            setError(error.response?.data?.message || "An error occurred");
        }
    };

    const fetchWorkedHours = async (modelId) => {
        try {
            const response = await api.get(`/production_real_hours/${modelId}/${selectedChain}`);
            console.log("response.data", response.data)
            setWorkedHoursByDate(response.data.worked_hours_by_date || {});
        } catch (error) {
          console.error("Error fetching worked hours:", error);
        }
      };
    
    return (
        <div className="production-volume min-w-0 mt-5">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold leading-tight text-gray-900 sm:text-2xl">Suivi de production en volume</h2>
                <div className="mb-4 flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                    <select
                        className="block w-full mt-4 outline-0 p-[.5rem] border border-[#b3b3b3] focus:border-2 focus:border-[#2684ff] rounded"
                        value={selectedChain}
                        onChange={handleSelectChange}
                    >
                        <option value="">Choisir une chaîne</option>
                        {chains.map((chain) => (
                            <option key={chain.id} value={chain.id}>
                                {chain.name}
                            </option>
                        ))}
                    </select>
                    <select
                        className="block w-full mt-4 outline-0 p-[.5rem] border border-[#b3b3b3] focus:border-2 focus:border-[#2684ff] rounded"
                        value={selectedModel}
                        onChange={handleModelChange}
                        disabled={!selectedChain || models.length === 0}
                    >
                        <option value="">Choisir un modèle</option>
                        {models.map((item, index) => (
                            <option key={item.id} value={item.id}>
                                {item.modele}{index === 0 ? " — Plus récent" : ""}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            {model && (
                <div className="mt-4 flex min-w-0 items-center gap-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-700 sm:text-sm">
                    {model.image && <img
                        src={`https://api.crosscheckconf.net${model.image}`}
                        alt={model.name || model.modele}
                        className="h-12 w-12 shrink-0 rounded object-cover"
                    />}
                    <div className="min-w-0">
                        <p className="break-words"><strong>Modèle :</strong> {model.modele}</p>
                        <p className="break-words"><strong>Catégorie :</strong> {model.category}</p>
                    </div>
                </div>
            )}
            {model && (
                <>
                <div className="mt-4 w-full max-w-full overflow-hidden rounded border border-slate-300 md:hidden">
                    <table className="mobile-production-table w-full table-fixed border-collapse bg-white text-[7px] leading-[9px] text-slate-800">
                        <colgroup>
                            <col className="w-[16%]" />
                            <col className="w-[10%]" />
                            <col className="w-[10.5%]" />
                            <col className="w-[10.5%]" />
                            <col className="w-[10.5%]" />
                            <col className="w-[10.5%]" />
                            <col className="w-[8%]" />
                            <col className="w-[12%]" />
                            <col className="w-[12%]" />
                        </colgroup>
                        <thead className="bg-slate-100 font-semibold text-slate-700">
                            <tr>
                                <th title="Date de production">Date</th>
                                <th title="Jour">Jour</th>
                                <th title="Production réelle">Prod.<br />réelle</th>
                                <th title="Production prévisionnelle">Prod.<br />prév.</th>
                                <th title="Cumul réel">Cumul<br />réel</th>
                                <th title="Cumul prévisionnel">Cumul<br />prév.</th>
                                <th title="Écart">Écart</th>
                                <th title="Nombre d'heures travaillées réel">Heures<br />réelles</th>
                                <th title="Nombre d'heures travaillées prévisionnel">Heures<br />prév.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {trackingRows.map((row) => (
                                <tr key={row.date}>
                                    <td className="font-semibold">{row.date.slice(5).replace("-", "/")}</td>
                                    <td className="capitalize">{row.day.slice(0, 3)}</td>
                                    <td>{row.realProduction}</td>
                                    <td>{row.plannedProduction}</td>
                                    <td>{row.cumulReal}</td>
                                    <td>{row.cumulPlanned}</td>
                                    <td className={row.difference < 0 ? "font-bold text-red-700" : "font-semibold"}>{row.difference}</td>
                                    <td>{row.realWorkedHours}</td>
                                    <td>{row.plannedWorkedHours}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="hidden md:block">
                <StickyTableContainer className="mt-5">
                    <table className="dashboard-sticky-table table-auto w-full min-w-[1100px] bg-slate-200 border-collapse border border-gray-300 shadow-md">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="sticky left-0 z-20 bg-gray-100 px-6 py-3 text-left text-xs font-medium text-[#4E4A4A] uppercase tracking-wider border border-gray-300">Date de production</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#4E4A4A] uppercase tracking-wider border border-gray-300">Jour</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#4E4A4A] uppercase tracking-wider border border-gray-300">Production Réelle</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#4E4A4A] uppercase tracking-wider border border-gray-300">Production prévisionnelle</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#4E4A4A] uppercase tracking-wider border border-gray-300">Cumul réél</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#4E4A4A] uppercase tracking-wider border border-gray-300">Cumul prévsionnel</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#4E4A4A] uppercase tracking-wider border border-gray-300">Ecart</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#4E4A4A] uppercase tracking-wider border border-gray-300">Nombre d'heures travaillés Réel</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-[#4E4A4A] uppercase tracking-wider border border-gray-300">Nombre d'heures travaillés Prévisionnel</th>
                            </tr>
                        </thead>
                        <tbody>
                            {trackingRows.map((row) => {
                                    return (
                                        <tr key={row.date}>
                                            <td className="sticky left-0 z-10 bg-slate-200 p-[2rem] text-left text-[#4E4A4A] font-semibold border border-gray-300">{row.date}</td>
                                            <td className="p-[2rem] text-left text-[#4E4A4A] font-semibold capitalize border border-gray-300">{row.day}</td>
                                            <td className="p-[2rem] text-left text-[#4E4A4A] font-semibold border border-gray-300">{row.realProduction}</td>
                                            <td className="p-[2rem] text-left text-[#4E4A4A] font-semibold border border-gray-300">{row.plannedProduction}</td>
                                            <td className="p-[2rem] text-left text-[#4E4A4A] font-semibold border border-gray-300">{row.cumulReal}</td>
                                            <td className="p-[2rem] text-left text-[#4E4A4A] font-semibold border border-gray-300">{row.cumulPlanned}</td>
                                            <td className="p-[2rem] text-left text-[#4E4A4A] font-semibold border border-gray-300">{row.difference}</td>
                                            <td className="p-[2rem] text-left text-[#4E4A4A] font-semibold border border-gray-300">{row.realWorkedHours}</td>
                                            <td className="p-[2rem] text-left text-[#4E4A4A] font-semibold border border-gray-300">{row.plannedWorkedHours}</td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </StickyTableContainer>
                </div>
                </>
            )}
        </div>
    );
};

export default SuivrDeProd;
